import React from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import type { MenuItemProps } from '@odin-labs/components';
import {
  Alert,
  EllipsisDropdownButton,
  getFixedWidthFaIcon,
} from '@odin-labs/components';
import { JobsiteWorkerOrientationStatus } from 'apollo/generated/client-operations';
import { to } from 'acl';
import { AuthContext } from 'auth';
import { Card, CardBody, CardFooter } from 'components/reactstrap';
import {
  faBriefcase,
  faCog,
  faEdit,
  faKey,
  faSync,
  faUserMinus,
  faUserPlus,
  faUserGear,
  faWrench,
} from 'components/icons';
import { ObjectHistory } from 'components/objectHistory';
import { ObjectHistorySource } from 'components/objectHistory/types';
import { ensureNonEmptyItems } from 'utils';
import {
  convertUTCtoLocalDate,
  getISODateTimeToFormattedDate,
} from 'utils/dates';
import { JobsiteWorker } from 'containers/worker/types';
import { JobsiteWorkerAccessComponent } from './jobsiteWorkerAccessComponent';
import { JobsiteWorkerDocumentStatus } from './jobsiteWorkerDocumentStatus';
import { JobsiteWorkerMedicalStatus } from './jobsiteWorkerMedicalStatus';
import { JobsiteWorkerOnboardingStatus } from './jobsiteWorkerOnboardingStatus';
import { JobsiteWorkerSiteSafetyStatus } from './jobsiteWorkerSiteSafetyStatus';
import { JobsiteWorkerCardProps } from './types';
import { classes } from './JobsiteWorkerCard.style';

const EditIcon = getFixedWidthFaIcon({ icon: faEdit });
const BriefcaseIcon = getFixedWidthFaIcon({ icon: faBriefcase });
const WrenchIcon = getFixedWidthFaIcon({ icon: faWrench });
const KeyIcon = getFixedWidthFaIcon({ icon: faKey });
const CogIcon = getFixedWidthFaIcon({ icon: faCog });
const SyncIcon = getFixedWidthFaIcon({ icon: faSync });
const UserMinusIcon = getFixedWidthFaIcon({ icon: faUserMinus });
const UserPlusIcon = getFixedWidthFaIcon({ icon: faUserPlus });
const UserGearIcon = getFixedWidthFaIcon({ icon: faUserGear });

export function JobsiteWorkerCard(
  props: JobsiteWorkerCardProps,
): React.ReactElement {
  const {
    worker,
    jobsiteWorker,
    openChangeWorkerAccessModal,
    openDocumentExemptModal,
    openAccessDebugModal,
    openEditJobsiteClearancesModal,
    syncWorkerAccess,
    confirmSelfOnboardingWorkerEdit,
    openEditJobsiteWorkerModal,
    openEnableJobsiteWorkerSelfOnboardingModal,
    openChangeContractorModal,
    openCorrectJobsiteAssignmentModal,
    disableActions,
  } = props;

  const { currentUser: user } = React.useContext(AuthContext);

  const hasAccess = jobsiteWorker?.currentAccess.isAllowed;
  const notAllowedReasons = jobsiteWorker?.currentAccess.notAllowedReason
    ?.split('\n')
    .filter(Boolean);
  const { jobsite } = jobsiteWorker?.jobsiteContractor ?? {};
  const { jobsiteId, clearanceAssignmentEnabled } = jobsite ?? {};

  const getSiteAccessText = (): React.ReactElement => {
    const endDateText = getISODateTimeToFormattedDate(
      convertUTCtoLocalDate(jobsiteWorker?.currentAccess?.endDate),
    );
    const siteAccessText = `Site access ${
      hasAccess ? 'expires' : 'expired'
    } on ${endDateText}`;
    return <p className={classes.siteAccessText}>{siteAccessText}</p>;
  };

  const isJobsiteWorkerOnboarded = !jobsiteWorker?.endDate;
  const isJobsiteWorkerBanned =
    jobsiteWorker?.isBanned && !jobsiteWorker?.banExpiresAt;
  // Server-calculated: the suspension has not yet expired in the jobsite's own
  // time zone. Never re-derive this from banExpiresAt in the browser (sc-17485).
  const isJobsiteWorkerSuspended = jobsiteWorker?.isSuspended;
  const isWorkerAllowedOnsite =
    isJobsiteWorkerOnboarded &&
    !isJobsiteWorkerBanned &&
    !isJobsiteWorkerSuspended;

  const isRevokeAccessItemVisible =
    hasAccess &&
    ((isJobsiteWorkerOnboarded &&
      user.isAllowed(to.offboardWorkers, jobsiteId)) ||
      (!isJobsiteWorkerSuspended &&
        user.isAllowed(to.suspendWorkers, jobsiteId)) ||
      (!isJobsiteWorkerBanned && user.isAllowed(to.banWorkers, jobsiteId)));
  const isRestoreAccessItemVisible =
    (!isJobsiteWorkerOnboarded &&
      user.isAllowed(to.offboardWorkers, jobsiteId)) ||
    (isJobsiteWorkerSuspended &&
      user.isAllowed(to.unsuspendWorkers, jobsiteId)) ||
    (isJobsiteWorkerBanned && user.isAllowed(to.unbanWorkers, jobsiteId));

  const isEnableJobsiteWorkerSelfOnboardingVisible =
    user.isAllowed(to.enableJobsiteWorkerSelfOnboarding) &&
    jobsiteWorker?.orientationStatus !==
      JobsiteWorkerOrientationStatus.SelfOnboarding;

  const confirmSelfOnboardingWorkerEditIfNeeded = ({
    then,
  }: {
    then: (jobsiteWorker: JobsiteWorker) => void;
  }): void => {
    if (
      jobsiteWorker.orientationStatus ===
      JobsiteWorkerOrientationStatus.SelfOnboarding
    ) {
      confirmSelfOnboardingWorkerEdit({ then: () => then(jobsiteWorker) });
    } else {
      then(jobsiteWorker);
    }
  };

  const menuItems = ensureNonEmptyItems<MenuItemProps>([
    {
      icon: EditIcon,
      text: 'Edit Worker Data',
      theme: 'secondary',
      onClick: (): void =>
        confirmSelfOnboardingWorkerEditIfNeeded({
          then: openEditJobsiteWorkerModal,
        }),
    },
    user.isAllowed(to.changeContractor) && {
      icon: WrenchIcon,
      text: 'Change Contractor',
      theme: 'secondary',
      onClick: (): void =>
        confirmSelfOnboardingWorkerEditIfNeeded({
          then: openChangeContractorModal,
        }),
    },
    user.isAllowed(to.correctJobsite) && {
      icon: BriefcaseIcon,
      text: 'Correct Jobsite',
      theme: 'secondary',
      onClick: (): void =>
        confirmSelfOnboardingWorkerEditIfNeeded({
          then: openCorrectJobsiteAssignmentModal,
        }),
    },
    user.isAllowed(to.editJobsiteAccess) &&
      clearanceAssignmentEnabled && {
        icon: KeyIcon,
        text: 'Edit Jobsite Access',
        theme: 'secondary',
        onClick: (): void => openEditJobsiteClearancesModal(jobsiteWorker),
      },
    user.isAllowed(to.debugWorkerAccess) && {
      icon: CogIcon,
      text: 'Debug Worker Access',
      theme: 'secondary',
      onClick: (): void => openAccessDebugModal(jobsiteWorker),
    },
    {
      icon: SyncIcon,
      text: 'Sync Worker Access',
      theme: 'secondary',
      onClick: syncWorkerAccess,
    },
    isWorkerAllowedOnsite &&
      isRevokeAccessItemVisible && {
        icon: UserMinusIcon,
        text: 'Revoke Access',
        theme: 'secondary',
        onClick: (): void => openChangeWorkerAccessModal(jobsiteWorker),
      },
    !isWorkerAllowedOnsite &&
      isRestoreAccessItemVisible && {
        icon: UserPlusIcon,
        text: 'Restore Access',
        theme: 'secondary',
        onClick: (): void => openChangeWorkerAccessModal(jobsiteWorker),
      },
    isEnableJobsiteWorkerSelfOnboardingVisible && {
      icon: UserGearIcon,
      text: 'Return to self onboarding',
      theme: 'secondary',
      onClick: (): void =>
        openEnableJobsiteWorkerSelfOnboardingModal(jobsiteWorker),
    },
  ]);

  const { contractorId, organization } =
    jobsiteWorker?.jobsiteContractor?.contractor ?? {};
  const contractorName = organization?.name;
  const jobsiteName = jobsite?.name;

  const trade = jobsiteWorker?.trade || 'N/A';
  const tradeExperience = jobsiteWorker?.tradeExperience || 'N/A';
  const jobTitle = jobsiteWorker?.jobTitle || 'N/A';
  const unionAffiliation = jobsiteWorker?.unionAffiliation || 'N/A';
  const classificationGroup = jobsiteWorker?.classificationGroup || 'N/A';
  const stickerNumber = jobsiteWorker?.stickerNumber || 'N/A';
  const payrollIdText = jobsiteWorker?.payrollId || 'N/A';
  const clearancesText = jobsiteWorker?.clearanceAssignments?.edges
    ?.map(({ node }) => node.clearance.name)
    ?.join(', ');
  const orientationDate =
    getISODateTimeToFormattedDate(jobsiteWorker?.orientationDate) || 'N/A';
  const onboardDate =
    getISODateTimeToFormattedDate(jobsiteWorker?.startDate) || 'N/A';
  const offboardDate =
    getISODateTimeToFormattedDate(jobsiteWorker?.endDate) || 'N/A';
  const escortPrivileges = jobsiteWorker?.isAntipassbackExempt ? 'Yes' : 'No';
  const inactivityExempt = jobsiteWorker?.isExemptFromSwipeInactivity
    ? 'Yes'
    : 'No';
  const skipRefresherTraining = jobsiteWorker?.isExemptFromSiteSpecificRefresher
    ? 'Yes'
    : 'No';

  const jobsiteLink = <Link to={`/jobsite/${jobsiteId}`}>{jobsiteName}</Link>;
  const contractorLink = (
    <Link to={`/contractor/${contractorId}`}>{contractorName}</Link>
  );

  return (
    <Card>
      <CardBody>
        <div className="odin-flex">
          <div
            className={`odin-mb-5 odin-grow odin-grid odin-grid-cols-1 
          sm:odin-grid-cols-3 sm:odin-gap-x-3`}
          >
            <div>
              <h1 className="odin-mt-1 odin-text-2.33xl odin-font-medium">
                <span>{jobsiteLink}</span>
              </h1>
              <h3 className="odin-my-1.5 odin-text-lg odin-font-medium">
                <span>{contractorLink}</span>
              </h3>
              <JobsiteWorkerAccessComponent
                jobsiteWorker={jobsiteWorker}
                type="badge"
                showEndDate
              />
              {getSiteAccessText()}
              <ObjectHistory
                className={cn(
                  classes.siteAccessText,
                  'align-self-center d-inline-block',
                )}
                object={jobsiteWorker}
                source={ObjectHistorySource.JobsiteWorker}
              />
            </div>
            <div className="odin-mt-1.5">
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>Trade</div>
                <div className={classes.jobsiteWorkerFieldValue}>{trade}</div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Trade experience
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {tradeExperience}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>Job title</div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {jobTitle}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Union affiliation
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {unionAffiliation}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Classification group
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {classificationGroup}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Sticker number
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {stickerNumber}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Payroll ID
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {payrollIdText}
                </div>
              </div>
              {user.isAllowed(to.editJobsiteAccess) &&
                clearanceAssignmentEnabled && (
                  <div className={classes.jobsiteWorkerField}>
                    <div className={classes.jobsiteWorkerFieldLabel}>
                      Clearances
                    </div>
                    <div className={classes.jobsiteWorkerFieldValue}>
                      {clearancesText}
                    </div>
                  </div>
                )}
              {jobsite?.jobsiteCustomWorkerDataFields?.edges
                ?.map((e) => e.node)
                // used to divide these fields between the two columns
                .filter((_, idx) => idx % 2 === 1)
                .map((customField) => (
                  <div
                    key={customField.fieldLabel}
                    className={classes.jobsiteWorkerField}
                  >
                    <div className={classes.jobsiteWorkerFieldLabel}>
                      {customField.fieldLabel}
                    </div>
                    <div className={classes.jobsiteWorkerFieldValue}>
                      {(
                        jobsiteWorker.customWorkerData as Record<string, string>
                      )?.[customField.fieldLabel] || 'N/A'}
                    </div>
                  </div>
                ))}
            </div>
            <div className="odin-mt-1.5">
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Orientation date
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {orientationDate}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Onboard date
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {onboardDate}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Offboard date
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {offboardDate}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Escort privileges
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {escortPrivileges}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Inactivity exempt
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {inactivityExempt}
                </div>
              </div>
              <div className={classes.jobsiteWorkerField}>
                <div className={classes.jobsiteWorkerFieldLabel}>
                  Skip refresher training
                </div>
                <div className={classes.jobsiteWorkerFieldValue}>
                  {skipRefresherTraining}
                </div>
              </div>
              {jobsite?.jobsiteCustomWorkerDataFields?.edges
                ?.map((e) => e.node)
                // used to divide these fields between the two columns
                .filter((_, idx) => idx % 2 === 0)
                .map((customField) => (
                  <div
                    key={customField.fieldLabel}
                    className={classes.jobsiteWorkerField}
                  >
                    <div className={classes.jobsiteWorkerFieldLabel}>
                      {customField.fieldLabel}
                    </div>
                    <div className={classes.jobsiteWorkerFieldValue}>
                      {(
                        jobsiteWorker.customWorkerData as Record<string, string>
                      )?.[customField.fieldLabel] || 'N/A'}
                    </div>
                  </div>
                ))}
            </div>
          </div>
          {!disableActions && (
            <div>
              <EllipsisDropdownButton
                menuItems={menuItems}
                popupWidthFitContent
              />
            </div>
          )}
        </div>

        <div className="odin-flex odin-flex-col odin-gap-y-2">
          {notAllowedReasons?.map((reason) => {
            return <Alert key={reason} text={reason} />;
          })}
        </div>
      </CardBody>
      <CardFooter>
        <div className="odin-flex">
          <div className="odin-w-full sm:odin-w-4/12">
            <p className={classes.footerStatus}>Onboarding status</p>
            <JobsiteWorkerOnboardingStatus
              jobsiteWorker={jobsiteWorker}
              confirmSelfOnboardingWorkerEdit={confirmSelfOnboardingWorkerEdit}
              openDocumentExemptModal={openDocumentExemptModal}
              disableActions={disableActions}
              showLabel
            />
          </div>
          <div className="odin-w-full sm:odin-w-8/12">
            <div className="odin-grid odin-grid-cols-1 sm:odin-grid-cols-3 odin-gap-3">
              <div>
                <p className={classes.footerStatus}>Site Orientation status</p>
                <JobsiteWorkerSiteSafetyStatus
                  jobsiteWorker={jobsiteWorker}
                  confirmSelfOnboardingWorkerEdit={
                    confirmSelfOnboardingWorkerEdit
                  }
                  disableActions={disableActions}
                  showLabel
                />
              </div>
              <div>
                <p className={classes.footerStatus}>Document status</p>
                <JobsiteWorkerDocumentStatus
                  worker={worker}
                  jobsiteWorker={jobsiteWorker}
                  confirmSelfOnboardingWorkerEdit={
                    confirmSelfOnboardingWorkerEdit
                  }
                  disableActions={disableActions}
                  showLabel
                />
              </div>
              <div>
                <p className={classes.footerStatus}>Medical status</p>
                <JobsiteWorkerMedicalStatus
                  worker={worker}
                  jobsiteWorker={jobsiteWorker}
                  confirmSelfOnboardingWorkerEdit={
                    confirmSelfOnboardingWorkerEdit
                  }
                  disableActions={disableActions}
                />
              </div>
            </div>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
